import {useEffect, useCallback} from 'react';
import {useLazyQuery} from '@apollo/client';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import getCountryCurrenciesQuery from '../graphql/getCountryCurrenciesQuery';
import {receivedCountryCurrencies} from '../store/slice';

interface UseCountryCurrenciesResult {
  currencies: string[] | null;
}

export const useCountryCurrencies = (): UseCountryCurrenciesResult => {
  const currencies = useAppSelector(({countries}) => countries.currencies);
  const dispatch = useAppDispatch();

  const [fetchCurrencies] = useLazyQuery(getCountryCurrenciesQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache'
  });

  const handleError = useCallback(
    (errorMessage: string) => {
      console.error(errorMessage);
      dispatch(receivedCountryCurrencies([]));
    },
    [dispatch]
  );

  const getCurrencies = useCallback(async () => {
    // Only fetch if currencies are missing
    if (Array.isArray(currencies)) return;

    try {
      const {data, error} = await fetchCurrencies();

      if (error) {
        const errorMessage = error.message ?? 'Failed to fetch currencies.';
        handleError(errorMessage);

        return;
      }

      const fetchedCurrencies = data?.countryCurrencies ?? [];
      dispatch(receivedCountryCurrencies(fetchedCurrencies));
    } catch (err) {
      handleError(`Unexpected error while fetching currencies: ${err}`);
    }
  }, [currencies, dispatch, fetchCurrencies, handleError]);

  useEffect(() => {
    getCurrencies();
  }, []); // eslint-disable-line

  return {currencies};
};
